/**
 * Active-phase resolver for CMS phase gating (CMS-012).
 *
 * This mirrors the BackOffice domain resolver (FestivalPhaseResolver.ResolveActivePhase) exactly:
 * same fixed sequence, same >= tie-break, same null-when-nothing-qualifies behaviour.
 *
 * Shared with the server, which needs it for the same reason the client does: the front page's
 * hero — and therefore its title and share card — changes with the phase, and a crawler must be
 * told the same one a visitor sees.
 */

/** Fixed CMS phase sequence (CMS-010). Order matters: it drives the tie-break and the after → teaser wrap. */
export const PHASE_SEQUENCE = ['teaser', 'weekly', 'lineup', 'days', 'schedule', 'during', 'after'];

/**
 * Returns the active phase for `now`: the phase whose start is the latest one at or before `now`,
 * evaluated in the fixed sequence above. When two phases share the same start instant, the one
 * later in the sequence wins (this is what makes the after → teaser wrap work).
 *
 * Returns null when no phase qualifies — i.e. `now` is before the earliest configured start, or
 * `phases` is absent/empty. Chronological ordering of the input is not assumed.
 */
export function resolveActivePhase(phases, now) {
  const starts = phases ?? [];
  const nowMs = now.getTime();

  let active = null;
  let latestMs = -Infinity;

  for (const phase of PHASE_SEQUENCE) {
    let maxForPhase = null;
    for (const p of starts) {
      if (p.phase !== phase) continue;
      const ms = new Date(p.startUtc).getTime();
      if (maxForPhase === null || ms > maxForPhase) maxForPhase = ms;
    }

    if (maxForPhase === null || maxForPhase > nowMs) continue;

    if (active === null || maxForPhase >= latestMs) {
      active = phase;
      latestMs = maxForPhase;
    }
  }

  return active;
}

/**
 * Resolves a page's hero for the active phase (CMS-044/CMS-038): finds the first phase variant
 * (published as a page-level sibling of `hero`, not nested inside it) whose `phases` includes it,
 * then merges field-by-field over the default hero — an unset variant field (undefined) falls back
 * to the default's value, matching the BackOffice editor's "empty fields inherit from Standard"
 * behavior. Pages without phase variants (every page but Home) always resolve to the default hero
 * unchanged.
 */
export function resolveHero(hero, phaseVariants, activePhase) {
  const variant = activeVariant(phaseVariants, activePhase);

  if (!variant) return hero;

  return {
    title: variant.title ?? hero.title,
    subtitle: variant.subtitle ?? hero.subtitle,
    image: variant.image !== undefined ? variant.image : hero.image,
    guitar: hero.guitar,
    design: variant.design ?? hero.design,
    primaryCta: variant.primaryCta ?? hero.primaryCta,
    secondaryCta: variant.secondaryCta ?? hero.secondaryCta,
  };
}

/**
 * Whether the page shows no hero in the active phase ("Ingen hero", Program 2027): the matching
 * phase variant says `hidden: true`, and the page starts with its first section. The hero itself
 * still resolves as before, so the title and share card keep coming from it.
 */
export function isHeroHidden(phaseVariants, activePhase) {
  return activeVariant(phaseVariants, activePhase)?.hidden === true;
}

/** The first phase variant that covers the active phase, if any. */
function activeVariant(phaseVariants, activePhase) {
  return activePhase === null
    ? undefined
    : phaseVariants?.find(v => v.phases.includes(activePhase));
}
